/**
 * Where the marketing site lives, for the page editor's preview links.
 *
 * The admin, the API and hpn-landing are deployed as environment pairs
 * (admindev ↔ apidev ↔ dev-landing, admin ↔ api ↔ happypetnet.com), and a
 * preview link has to land in the same environment the editor is saving to.
 * The only per-environment value baked into this bundle is
 * REACT_APP_BACKEND_URL (the API origin), so the site origin is derived from
 * it — the same rule my-pet-app applies in `helpers/siteUrl.ts` — rather than
 * from a second variable nobody remembers to set. That second variable is what
 * used to send editors to the retired pre-split Pages host (DAB-845).
 *
 * Hosts come from the generated shared-chrome contract, so a host change is a
 * chrome.json edit + sync, not an edit here. REACT_APP_LANDING_URL still wins
 * when set, for a one-off build against an unusual host.
 */
import { HOSTS, STAGING_HOSTS } from "constants/sharedChrome";

/** The landing container in the local docker stack (see the superproject Makefile). */
export const LOCAL_SITE_URL = "https://landing.happypetnet.test";

const hostnameOf = (value) => {
  if (!value) return "";
  try {
    return new URL(value).hostname;
  } catch {
    return "";
  }
};

const isLocalHostname = (hostname) =>
  hostname === "localhost" ||
  hostname === "127.0.0.1" ||
  hostname === "happypetnet.test" ||
  hostname.endsWith(".happypetnet.test");

/** Which deployment pair a bundle was built for, read off its API origin. */
export const environmentOf = (backendUrl) => {
  const hostname = hostnameOf(backendUrl);
  // Unset env => local dev; src/config.js defaults the API to localhost:4242.
  if (!hostname || isLocalHostname(hostname)) return "local";
  if (hostname.includes("apidev.")) return "staging";
  return "production";
};

const SITE_BY_ENV = {
  local: LOCAL_SITE_URL,
  staging: STAGING_HOSTS.site,
  production: HOSTS.site,
};

/**
 * Origin of the marketing site for the environment this bundle was built for.
 * Pure over its inputs so the rule is testable without touching process.env.
 */
export const resolveLandingUrl = ({ backendUrl, landingUrl } = {}) =>
  (landingUrl || "").replace(/\/+$/, "") || SITE_BY_ENV[environmentOf(backendUrl)];

export const getLandingUrl = () =>
  resolveLandingUrl({
    backendUrl: process.env.REACT_APP_BACKEND_URL,
    landingUrl: process.env.REACT_APP_LANDING_URL,
  });

/**
 * Origin of the live draft preview server (DAB-861) for this bundle's
 * environment. Messages from the preview frame are trusted only from here, and
 * a minted session URL on any other origin is refused. Not in the chrome
 * contract because only admin frames it. REACT_APP_LIVE_PREVIEW_URL wins when set.
 */
const PREVIEW_BY_ENV = {
  local: "https://preview.happypetnet.test",
  staging: "https://previewdev.happypetnet.com",
  production: "https://preview.happypetnet.com",
};

export const resolvePreviewOrigin = ({ backendUrl, previewUrl } = {}) => {
  if (previewUrl) {
    try {
      return new URL(previewUrl).origin;
    } catch {
      // Fall through to the environment default rather than trust a typo.
    }
  }
  return PREVIEW_BY_ENV[environmentOf(backendUrl)];
};

const LOCAL_HTTP_HOST = /^(localhost|127\.0\.0\.1)$|\.test$/;

/**
 * The preview origin, or undefined when it must not be framed. The frame is
 * sandboxed with `allow-same-origin`, which is only safe on another origin:
 * on admin's own it would run with admin's storage (the auth token). Plain
 * http is tolerated only on localhost and docker `.test` hosts.
 */
export const usablePreviewOrigin = (origin, adminOrigin) => {
  if (!origin) return undefined;
  let url;
  try {
    url = new URL(origin);
  } catch {
    return undefined;
  }
  const isSecure =
    url.protocol === "https:" || (url.protocol === "http:" && LOCAL_HTTP_HOST.test(url.hostname));
  if (!isSecure || url.origin === adminOrigin) return undefined;
  return url.origin;
};

export const getLivePreviewOrigin = () =>
  usablePreviewOrigin(
    resolvePreviewOrigin({
      backendUrl: process.env.REACT_APP_BACKEND_URL,
      previewUrl: process.env.REACT_APP_LIVE_PREVIEW_URL,
    }),
    window.location.origin,
  );

/**
 * A marketing-site URL with the locale prefix the site uses: Bulgarian is
 * served unprefixed, every other language lives under `/<lang>/`.
 * `landingPageUrl('/parents/', 'en')` => `https://happypetnet.com/en/parents/`.
 */
export const landingPageUrl = (route, lang, base = getLandingUrl()) => {
  const path = route || "/";
  return `${base}${lang === "bg" ? "" : `/${lang}`}${path}`;
};
